import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useGeneralSoContext } from 'src/common/context';
import { GetOfferQuery, GetOfferQueryVariables } from '../types/types';

/**
 * Get Offer by encrypted ID
 *
 * @param id - Encrypted Offer ID
 * @returns Full offer data including package selection, payment, and documents
 *
 * @example
 * const { data } = useQuery(getOffer, { variables: { id: encryptedOfferId } });
 */
export const getOffer = gql`
  query GetOffer($id: String!) {
    getOffer(id: $id) {
      _id
      offerNumber
      status
      soId
      productTypeId
      departmentCode
      productTypeSpecific {
        isPublished
        building {
          package {
            productId
            code
            label
            description
            propertyType
          }
          coverages {
            code
            label
            description
          }
        }
        household {
          package {
            productId
            code
            label
            description
            propertyType
          }
          coverages {
            code
            label
            description
          }
        }
        payment {
          basePremium
          discount
          voucher
          totalPremium
          singlePremium
          installmentsPremium
          paymentMethod
        }
        newPolicy {
          newPolicyStartDate
          policyDuration
          cancelContract
        }
          calculationData {
          calculationId
          packageCode
          packageName
          contractId
          sessionId
          insuredInterestId
          contractorId
          insuredPersonId
          nonStandardRequestToggle
          isFlood
          commercialDiscount
          coverages {
            code
            name
            description
            premium
            insuredAmount
            risks {
              code
              name
              description
              premium
              insuredAmount
              claims {
                name
                premium
                insuredAmount
              }
            }
          }
          additionalCoverages {
            code
            name
            description
            premium
            insuredAmount
            risks {
              code
              name
              description
              premium
              insuredAmount
              claims {
                name
                premium
                insuredAmount
              }
            }
          }
        }
        lastPaymentAttempt
        paidAt
        policyLengthType
        numberOfInstallments
        brandId {
          code
          name
        }
        shoppingCartId
        attachedDocuments
        termsAndConditions
      }

      createdAt
      updatedAt
      deleted
      archived
    }
  }
`;

export function useGetOfferById(id?: string) {
  const { offerId } = useGeneralSoContext();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const { data, loading, error, refetch } = useQuery<GetOfferQuery, GetOfferQueryVariables>(getOffer, {
    variables: { id: id || offerId! },
    skip: !offerId && !id
  });

  const isLoading = !isInitialized || loading;

  return {
    offer: (id || offerId) ? data?.getOffer : undefined,
    loading: isLoading,
    error,
    refetch,
    offerId
  };
}
